import { useEffect, useImperativeHandle, useRef, type Ref } from 'react';

export interface SignaturePadHandle {
  clear: () => void;
  isEmpty: () => boolean;
  canvas: () => HTMLCanvasElement | null;
}

/**
 * Подпис с пръст върху стъклото. Рисува се в реални пиксели на устройството,
 * за да не излезе стъпаловидна линия на екран с двойна плътност — подписът е
 * доказателство и трябва да е четим и след печат.
 */
export function SignaturePad({ ref }: { ref: Ref<SignaturePadHandle> }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawingRef = useRef(false);
  const emptyRef = useRef(true);
  const lastRef = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = Math.min(3, window.devicePixelRatio || 1);
    const rect = canvas.getBoundingClientRect();
    canvas.width = Math.round(rect.width * ratio);
    canvas.height = Math.round(rect.height * ratio);
    const context = canvas.getContext('2d');
    if (!context) return;
    context.scale(ratio, ratio);
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, rect.width, rect.height);
    context.lineWidth = 3;
    context.lineCap = 'round';
    context.lineJoin = 'round';
    context.strokeStyle = '#101828';
  }, []);

  useImperativeHandle(ref, () => ({
    clear: () => {
      const canvas = canvasRef.current;
      const context = canvas?.getContext('2d');
      if (!canvas || !context) return;
      const ratio = Math.min(3, window.devicePixelRatio || 1);
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, canvas.width / ratio, canvas.height / ratio);
      emptyRef.current = true;
    },
    isEmpty: () => emptyRef.current,
    canvas: () => canvasRef.current,
  }));

  function pointOf(event: React.PointerEvent<HTMLCanvasElement>) {
    const rect = event.currentTarget.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  }

  return (
    <canvas
      ref={canvasRef}
      className="no-select h-56 w-full touch-none rounded-2xl border-2 border-line bg-white"
      onPointerDown={(event) => {
        event.currentTarget.setPointerCapture(event.pointerId);
        drawingRef.current = true;
        lastRef.current = pointOf(event);
      }}
      onPointerMove={(event) => {
        if (!drawingRef.current) return;
        const context = event.currentTarget.getContext('2d');
        const last = lastRef.current;
        const next = pointOf(event);
        if (!context || !last) return;
        context.beginPath();
        context.moveTo(last.x, last.y);
        context.lineTo(next.x, next.y);
        context.stroke();
        lastRef.current = next;
        emptyRef.current = false;
      }}
      onPointerUp={() => {
        drawingRef.current = false;
        lastRef.current = null;
      }}
      onPointerLeave={() => {
        drawingRef.current = false;
        lastRef.current = null;
      }}
    />
  );
}
